import * as React from 'react';
import type {
  BravoWriteResult,
  PacingIntent,
  UpdateLineItemsToolInput
} from 'shared/src/api/ai-line-item-chat-types';
import type { ResolvedPacing } from 'shared/src/domain/line-item/pacing-patch';
import { formatRange } from 'shared/src/date-string-utils/date-utils';
import { Button } from '@/components/forms/ui/button';
import { formatCurrency } from '@/components/tables/table-utils';
import { trpc } from '@/utils/trpc-client';
import { showErrorToast } from '@/components/feedback/error-toast';
import { CommitCardFrame, type CommitOutcome } from '@/features/ai/shared/commit-card-frame';
import {
  applyPacingOverride,
  buildBatchUpdateRows,
  hasBlockingIssues,
  type BatchUpdateRow
} from './batch-update-rows';

export type LineItemBatchUpdateCardProps = {
  toolCallId: string;
  input: UpdateLineItemsToolInput;
  proposalId: string;
  /** Outcome of a resolution made outside this card (prior session, or the modal's soft-cancel). */
  externalOutcome?: CommitOutcome | null;
  onResolve: (output: BravoWriteResult) => void | Promise<void>;
  // Escape hatch: close the modal and open the full line-item form for this row.
  onEditLineItem?: (lineItemId: string) => void;
};

/**
 * Confirmation card for the `updateLineItems` batch confirmed write. Runs the shared pacing
 * validator client-side (via buildBatchUpdateRows) so blocking problems are visible BEFORE
 * Confirm, then applies every edit in one deterministic tRPC call. Resolves the tool on every
 * outcome: success/cancel with the result, failure with an error output so the agent can explain
 * what went wrong and re-propose. Rendered in the shared CommitCardFrame: staged tier normally,
 * escalated to the live tier when any edit routes to the live execution line item.
 */
export function LineItemBatchUpdateCard(props: LineItemBatchUpdateCardProps): JSX.Element {
  const utils = trpc.useUtils();
  const { data } = trpc.getProposalLineItems.useQuery({ proposal_id: props.proposalId });
  const batchMutation = trpc.bravoUpdateLineItems.useMutation();
  const [outcome, setOutcome] = React.useState<CommitOutcome | null>(null);
  // One-click deterministic fixes: a local pacing-intent override per row, fed back through the
  // same shared resolver so the preview and the write stay identical.
  const [pacingOverrides, setPacingOverrides] = React.useState<
    Record<string, PacingIntent | undefined>
  >({});

  const currentById = React.useMemo(
    () => Object.fromEntries((data?.data ?? []).map(li => [li.id, li])),
    [data]
  );
  const rows = React.useMemo(
    () => buildBatchUpdateRows(props.input.edits, currentById, pacingOverrides),
    [props.input.edits, currentById, pacingOverrides]
  );

  async function handleConfirm() {
    try {
      const res = await batchMutation.mutateAsync({
        proposal_id: props.proposalId,
        tool_call_id: props.toolCallId,
        edits: props.input.edits.map(e => ({
          line_item_id: e.line_item_id,
          patch: applyPacingOverride(e, pacingOverrides)
        }))
      });
      setOutcome({
        status: 'applied',
        summary: `${res.updated_count} line item${res.updated_count === 1 ? '' : 's'} updated`
      });
      await utils.getProposalLineItems.invalidate();
      await utils.campaignV2.invalidate();
      await utils.proposalDiff.invalidate();
      await props.onResolve({ status: 'updated', updated_count: res.updated_count });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to apply the changes';
      setOutcome({ status: 'failed', summary: message });
      showErrorToast(message);
      // All-or-nothing on the server: nothing was written, so a corrected re-proposal is safe.
      await props.onResolve({ status: 'failed', error: message });
    }
  }

  async function handleCancel() {
    setOutcome({ status: 'cancelled' });
    await props.onResolve({ status: 'cancelled' });
  }

  function handleApplyMonthlyFix(lineItemId: string) {
    setPacingOverrides(prev => ({ ...prev, [lineItemId]: { mode: 'monthly' } }));
  }

  return (
    <LineItemBatchUpdateCardView
      rows={rows}
      loading={!data}
      toolCallId={props.toolCallId}
      outcome={outcome}
      externalOutcome={props.externalOutcome ?? null}
      isPending={batchMutation.isPending}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      onApplyMonthlyFix={handleApplyMonthlyFix}
      onEditLineItem={props.onEditLineItem}
    />
  );
}

export type LineItemBatchUpdateCardViewProps = {
  rows: BatchUpdateRow[];
  loading: boolean;
  toolCallId?: string;
  outcome: CommitOutcome | null;
  externalOutcome: CommitOutcome | null;
  isPending: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
  onApplyMonthlyFix: (lineItemId: string) => void;
  onEditLineItem?: (lineItemId: string) => void;
};

/** Pure, presentational — no tRPC. Exported for RTL tests against real props. */
export function LineItemBatchUpdateCardView({
  rows,
  loading,
  toolCallId,
  outcome,
  externalOutcome,
  isPending,
  onConfirm,
  onCancel,
  onApplyMonthlyFix,
  onEditLineItem
}: LineItemBatchUpdateCardViewProps): JSX.Element {
  const live = rows.some(r => r.routesToExecution);
  return (
    <CommitCardFrame
      tier={live ? 'live' : 'staged'}
      title={`Edit ${rows.length} line item${rows.length > 1 ? 's' : ''}`}
      consequence={
        live
          ? '⚡-marked changes apply to the live execution line item immediately.'
          : 'Updates this proposal — nothing goes live until you promote.'
      }
      confirmLabel="Apply changes"
      confirmDisabled={loading || hasBlockingIssues(rows)}
      isPending={isPending}
      outcome={outcome}
      externalOutcome={externalOutcome}
      toolCallId={toolCallId}
      onConfirm={onConfirm}
      onCancel={onCancel}>
      {loading ? (
        <p className="text-sm text-gray-500">Loading line items…</p>
      ) : (
        rows.map((row, i) => (
          <BatchUpdateRowView
            key={row.lineItemId}
            row={row}
            isFirst={i === 0}
            onApplyMonthlyFix={onApplyMonthlyFix}
            onEditLineItem={onEditLineItem}
          />
        ))
      )}
    </CommitCardFrame>
  );
}

function BatchUpdateRowView({
  row,
  isFirst,
  onApplyMonthlyFix,
  onEditLineItem
}: {
  row: BatchUpdateRow;
  isFirst: boolean;
  onApplyMonthlyFix: (lineItemId: string) => void;
  onEditLineItem?: (lineItemId: string) => void;
}): JSX.Element {
  return (
    <div className={isFirst ? '' : 'mt-3 border-t pt-3'}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-gray-900">{row.name}</div>
        {onEditLineItem && !row.missing ? (
          <button
            type="button"
            onClick={() => onEditLineItem(row.lineItemId)}
            className="text-xs font-medium text-blue-600 hover:underline">
            Open in line-item form
          </button>
        ) : null}
      </div>

      {row.fieldChanges.length > 0 ? (
        <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {row.fieldChanges.map(change => (
            <React.Fragment key={change.field}>
              <dt className="text-gray-500">
                {change.field}
                {change.live ? (
                  <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-semibold text-amber-800">
                    ⚡ live
                  </span>
                ) : null}
              </dt>
              <dd>
                <span className="text-gray-400">{change.before}</span>
                {' → '}
                <span className="font-medium text-gray-900">{change.after}</span>
              </dd>
            </React.Fragment>
          ))}
        </dl>
      ) : null}

      {row.resolvedPacing ? <ResolvedPacingView pacing={row.resolvedPacing} /> : null}

      {row.errors.map(issue => (
        <div key={`${issue.path}-error`} className="mt-1 text-xs text-red-600">
          {issue.message}
        </div>
      ))}
      {row.warnings.map(issue => (
        <div key={`${issue.path}-warning`} className="mt-1 text-xs text-amber-700">
          {issue.message}
        </div>
      ))}

      {row.canOfferMonthlyFix ? (
        <Button
          onClick={() => onApplyMonthlyFix(row.lineItemId)}
          size="sm"
          variant="secondary"
          className="mt-2">
          Regenerate monthly blocks over the new dates
        </Button>
      ) : null}
    </div>
  );
}

function ResolvedPacingView({ pacing }: { pacing: ResolvedPacing }): JSX.Element {
  return (
    <div className="mt-2">
      <div className="text-xs font-medium text-gray-500">Pacing</div>
      <div className="mt-1 flex flex-col gap-1">
        {pacing.pacing_details.map((block, i) => (
          <div key={`${block.dates.from}-${i}`} className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-xs text-gray-500">
              {pacing.pacing_type === 'lifetime' ? 'Lifetime' : `Block ${i + 1}`}
            </span>
            <span className="flex rounded-md bg-gray-100 px-2 py-1 text-sm">
              {formatRange(block.dates)}
            </span>
            <span className="flex rounded-md bg-gray-100 px-2 py-1 text-sm">
              {formatCurrency(block.budget)}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-1 text-xs text-gray-500">
        {pacing.monthly_rollover ? 'Rollover' : 'No rollover'}
      </div>
    </div>
  );
}
